import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Users, Download, AlertTriangle, Trash2 } from 'react-feather';
import { Breadcrumbs } from '../../../AbstractElements';
import { fetchUsers, fetchUserByUserId, deleteUserByUserId, exportUsers, fetchUsersByQuiz } from '../../../api/userQuiz';
import { toast } from 'react-toastify';

// Import components
import StatisticsCards from './StatisticsCards';
import FilterBar from './FilterBar';
import UsersTable from './UsersTable';
import Pagination from './Pagination';
import UserDetailModal from './UserDetailModal';
import EmptyState from './EmptyState';
import LoadingState from './LoadingState';
import ExportModal from './ExportModal';

const UserQuizDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  
  // Filters
  const [filters, setFilters] = useState({
    role_code: '',
    followedOA: '',
    search: '',
    quizName: '',
    quizId: ''
  });

  // Quiz info state (khi filter theo quizId)
  const [quizInfo, setQuizInfo] = useState(null);
  const [isQuizMode, setIsQuizMode] = useState(false); // Flag để biết đang ở mode quiz hay không

  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Export states
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Delete states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load users on mount and when filters change
  useEffect(() => {
    loadUsers();
  }, [pagination.page, pagination.limit]);

  // Load users function
  const loadUsers = async () => {
    setLoading(true);
    try {
      // Nếu có quizId, gọi API mới
      if (filters.quizId) {
        const quizId = parseInt(filters.quizId);
        const response = await fetchUsersByQuiz(quizId, {
          page: pagination.page,
          limit: pagination.limit
        });
        
        if (response.success) {
          // Lưu quiz info
          setQuizInfo(response.data.quiz);
          setIsQuizMode(true);
          
          // Transform data structure: response.data.users là array có {rank, user, attempt}
          const transformedUsers = response.data.users.map(item => ({
            ...item.user,
            rank: item.rank,
            attempt: item.attempt
          }));
          
          setUsers(transformedUsers);
          setPagination(response.data.pagination);
          toast.success('✅ Tải danh sách người dùng theo quiz thành công!');
        }
      } else {
        // Gọi API cũ nếu không có quizId
        setIsQuizMode(false);
        setQuizInfo(null);
        
        const params = {
          page: pagination.page,
          limit: pagination.limit
        };

        if (filters.role_code) params.role_code = filters.role_code;
        if (filters.followedOA !== '') params.followedOA = filters.followedOA === 'true';
        if (filters.quizName) params.quizName = filters.quizName;

        const response = await fetchUsers(params);
        
        if (response.success) {
          setUsers(response.data.users);
          setPagination(response.data.pagination);
          toast.success('✅ Tải danh sách người dùng thành công!');
        }
      }
    } catch (error) {
      toast.error('❌ Lỗi khi tải dữ liệu: ' + error.message);
      setUsers([]);
      setQuizInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadUsers();
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      role_code: '',
      followedOA: '',
      search: '',
      quizName: '',
      quizId: ''
    });
    setQuizInfo(null);
    setIsQuizMode(false);
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => loadUsers(), 100);
  };

  // View user detail
  const handleViewDetail = async (userId) => {
    setLoadingDetail(true);
    setDetailModalOpen(true);
    try {
      const response = await fetchUserByUserId(userId);
      if (response.success) {
        setSelectedUser(response.data.user);
      }
    } catch (error) {
      toast.error('❌ Lỗi khi tải thông tin: ' + error.message);
      setDetailModalOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle delete user
  const handleDeleteClick = (userId, userName) => {
    setUserToDelete({ userId, userName });
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      await deleteUserByUserId(userToDelete.userId);
      toast.success(`✅ Đã xóa user "${userToDelete.userName}" thành công!`);
      setDeleteModalOpen(false);
      setUserToDelete(null);
      
      // Refresh user list
      await loadUsers();
    } catch (error) {
      toast.error('❌ Lỗi khi xóa user: ' + (error.message || 'Có lỗi xảy ra'));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
  };

  // Handle export
  const handleExport = async (exportParams) => {
    setExporting(true);
    try {
      await exportUsers(exportParams);
      toast.success('✅ Export thành công! File đã được tải về máy.');
      setExportModalOpen(false);
    } catch (error) {
      console.error('Export error details:', error);
      
      // Build detailed error message
      let errorMessage = error.message || 'Có lỗi xảy ra';
      
      // Add status code if available
      if (error.status) {
        errorMessage += ` (Status: ${error.status})`;
        
        // Provide helpful messages for common status codes
        if (error.status === 404) {
          errorMessage += ' - Endpoint không tồn tại. Vui lòng kiểm tra lại API endpoint.';
        } else if (error.status === 401 || error.status === 403) {
          errorMessage += ' - Không có quyền truy cập. Vui lòng kiểm tra lại authentication.';
        } else if (error.status >= 500) {
          errorMessage += ' - Lỗi server. Vui lòng thử lại sau.';
        }
      }
      
      // Add content type info if available
      if (error.contentType) {
        errorMessage += ` [Content-Type: ${error.contentType}]`;
      }
      
      toast.error(`❌ Lỗi khi export: ${errorMessage}`);
      
      // Log HTML preview for debugging if available
      if (error.htmlPreview) {
        console.error('HTML Response Preview:', error.htmlPreview);
      }
    } finally {
      setExporting(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter users locally by search (chỉ áp dụng khi không phải quiz mode)
  const filteredUsers = filters.search && !isQuizMode
    ? users.filter(user => 
        user.userId?.toLowerCase().includes(filters.search.toLowerCase()) ||
        user.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        user.phone?.toLowerCase().includes(filters.search.toLowerCase())
      )
    : users;

  // Statistics
  const stats = {
    total: pagination.total,
    followed: users.filter(u => u.followedOA).length,
    notFollowed: users.filter(u => !u.followedOA).length
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title="User Quiz" mainTitle="Quản lý User Quiz" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            {/* Info Banner */}
            <Card className="bg-gradient-primary text-white mb-4 border-0 shadow">
              <CardBody className="p-4">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 rounded-circle p-3 me-3">
                    <Users size={32} />
                  </div>
                  <div>
                    <h4 className="text-white mb-1">
                      {quizInfo ? `Quiz: ${quizInfo.name}` : 'User Quiz Management'}
                    </h4>
                    <p className="mb-0 opacity-75">
                      {quizInfo 
                        ? `Danh sách người dùng đã làm quiz (ID: ${quizInfo.id})`
                        : 'Quản lý người dùng tham gia quiz'
                      }
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Statistics Cards */}
            {!loading && users.length > 0 && (
              <StatisticsCards stats={stats} />
            )}

            {/* Main Content Card */}
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header with Filters */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">
                      <Users size={24} className="me-2 text-primary" />
                      Danh sách User Quiz
                    </h4>
                    <Button 
                      color="success" 
                      onClick={() => setExportModalOpen(true)}
                      disabled={loading || filteredUsers.length === 0}
                    >
                      <Download size={16} className="me-1" />
                      Export Excel
                    </Button>
                  </div>

                  {/* Filters Section */}
                  <FilterBar
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={handleResetFilters}
                  />
                </div>

                {/* Loading State */}
                {loading && <LoadingState />}

                {/* Empty State */}
                {!loading && filteredUsers.length === 0 && <EmptyState />}

                {/* Users Table */}
                {!loading && filteredUsers.length > 0 && (
                  <>
                    <UsersTable
                      users={filteredUsers}
                      pagination={pagination}
                      onViewDetail={handleViewDetail}
                      onDelete={handleDeleteClick}
                      formatDate={formatDate}
                      isQuizMode={isQuizMode}
                    />

                    {/* Pagination */}
                    <Pagination
                      pagination={pagination}
                      onPageChange={handlePageChange}
                      onLimitChange={handleLimitChange}
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* User Detail Modal */}
        <UserDetailModal
          isOpen={detailModalOpen}
          toggle={() => setDetailModalOpen(false)}
          user={selectedUser}
          loading={loadingDetail}
          formatDate={formatDate}
        />

        {/* Export Modal */}
        <ExportModal
          isOpen={exportModalOpen}
          toggle={() => setExportModalOpen(false)}
          onExport={handleExport}
          currentFilters={filters}
          exporting={exporting}
        />

        {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered>
          <ModalHeader toggle={handleCancelDelete} className="bg-danger text-white">
            <AlertTriangle size={20} className="me-2" />
            Xác nhận xóa user
          </ModalHeader>
          <ModalBody className="p-4">
            <div className="d-flex align-items-center mb-3">
              <div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
                <AlertTriangle size={32} className="text-danger" />
              </div>
              <div>
                <h5 className="mb-1">Bạn có chắc chắn muốn xóa?</h5>
                <p className="mb-0 text-muted">
                  User: <strong>{userToDelete?.userName || userToDelete?.userId}</strong>
                </p>
                <p className="mb-0 text-muted small mt-2">
                  ID: <code className="text-danger">{userToDelete?.userId}</code>
                </p>
              </div>
            </div>
            <div className="alert alert-warning mb-0">
              <strong>⚠️ Cảnh báo:</strong> Hành động này không thể hoàn tác. Tất cả dữ liệu liên quan đến user này sẽ bị xóa vĩnh viễn.
            </div>
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button 
              color="secondary" 
              onClick={handleCancelDelete}
              disabled={deleting}
            >
              Hủy
            </Button>
            <Button 
              color="danger" 
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Đang xóa...
                </>
              ) : (
                <>
                  <Trash2 size={16} className="me-1" />
                  Xóa
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>

      {/* Custom CSS */}
      <style>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        }
        .table th {
          font-weight: 600;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hover-row:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </Fragment>
  );
};

export default UserQuizDashboard;
